import React, { useState } from 'react';
import Dialog from '../../organisms/Dialog';
import TextEdit from '../../molecules/TextEdit';
import { closeOperation, CloseOperationRequest } from '../../../services/CloseOperationService';
import { toast } from 'react-toastify';
import { formatCurrency } from './structuringUtils';

interface CreateOperationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete?: () => void;
    operationId?: string;
    operationSummary?: {
        totalAmount: number;
        totalDocuments: number;
        totalReceivables: number;
    };
}

const CreateOperationModal: React.FC<CreateOperationModalProps> = (props) => {
    const { isOpen, onClose, onComplete, operationId, operationSummary } = props;
    const [formData, setFormData] = useState({ operationCode: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [invalidReceivables, setInvalidReceivables] = useState<{ document_id: string; document_title: string; receivable_number: string }[]>([]);
    const [errorMessage, setErrorMessage] = useState('');

    React.useEffect(() => {
        if (!isOpen) {
            setFormData({ operationCode: '' });
            setInvalidReceivables([]);
            setErrorMessage('');
        }
    }, [isOpen]);
    const handleSubmit = async () => {
        if (!operationId) {
            toast.error('ID da operação não encontrado');
            return;
        }

        // Validação: Código do contrato não pode estar em branco
        if (!formData.operationCode || formData.operationCode.trim() === '') {
            toast.error('O código do contrato deve ser preenchido.');
            return;
        }

        // Validação: Código do contrato deve ser numérico, opcionalmente com sufixo -N (ex: 4612 ou 4612-1)
        if (!/^\d+(-\d+)?$/.test(formData.operationCode.trim())) {
            toast.error('O código do contrato deve estar no formato numérico (ex: 4612 ou 4612-1).');
            return;
        }

        setIsSubmitting(true);

        try {
            const requestData: CloseOperationRequest = {
                contract_code: formData.operationCode,
            };

            const result = await closeOperation(operationId, requestData);

            if (result.success) {
                toast.success(result.message);
                setInvalidReceivables([]);
                setErrorMessage('');
                if (onComplete) {
                    onComplete();
                }
                onClose();
            } else {
                setErrorMessage(result.message);
                setInvalidReceivables(result.invalid_receivables || []);
                toast.error(result.message);
            }
        } catch (error: any) {
            console.error('Erro ao fechar operação:', error);
            toast.error('Erro ao fechar operação. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Dialog
            id="create-operation-modal"
            show={isOpen}
            onClose={onClose}
            header="Fechar Lote"
            body={
                <div className="space-y-6 p-6">
                    {/* Resumo da Operação */}
                    {operationSummary && (
                        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                            <h3 className="text-lg font-medium mb-4">Resumo da Operação</h3>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Valor Total
                                    </label>
                                    <p className="text-lg font-semibold">
                                        {formatCurrency(operationSummary.totalAmount)}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Total de Documentos
                                    </label>
                                    <p className="text-lg font-semibold">
                                        {operationSummary.totalDocuments}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Total de Recebíveis
                                    </label>
                                    <p className="text-lg font-semibold">
                                        {operationSummary.totalReceivables}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Formulário */}
                    <div className="space-y-4">
                        <TextEdit
                            label="Código da Operação"
                            value={formData.operationCode}
                            onChange={(e) => handleInputChange('operationCode', e.target.value)}
                        />
                    </div>

                    {errorMessage && invalidReceivables.length > 0 && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-600 rounded-lg p-4">
                            <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">{errorMessage}</p>
                            <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                                {invalidReceivables.map((item, idx) => (
                                    <li key={idx} className="flex items-center space-x-2">
                                        <span>•</span>
                                        <span>Documento <strong>{item.document_title}</strong> — Parcela <strong>{item.receivable_number}</strong></span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            }
            footer={
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        {isSubmitting ? 'Fechando...' : 'Concluir'}
                    </button>
                </div>
            }
        />
    );
};

export default CreateOperationModal;
